/**
 * @file plan/index.ts
 * @description Plan service to manage all the plan/payment plan related operations.
 *
 * @author smendonca
 *
 */

/**
 * Imports
 */
import * as CustomerModels from '../../functions/customer/customer.model';
import { getPlan } from '../../functions/paymentPlan';
import * as PaymentModels from '../../functions/paymentPlan/paymentPlan.model';
import * as ProductModels from '../../functions/product/product.model';
import { Surcharge } from '../../functions/quotes/quotes.model';
import * as _cache from '../cache';
import { financeDetails, getDiscount } from '../contract';
import { isoDate, now, timestamp } from '../datetime';
import { getCoverageItems } from '../surcharges';
import { updateOptions } from '../surcharges/options';
import * as Models from './model';

/**
 * Sets the payment plan dadta.
 */
export const setPaymentPlan = async (
  plan: Models.IPlan | undefined = undefined,
  product: ProductModels.default | undefined = undefined
): Promise<PaymentModels.Plan> => {
  plan = plan ?? _cache.getItem<Models.IPlan>(_cache.Key.plan);
  product =
    product ?? _cache.getItem<ProductModels.default>(_cache.Key.product);
  const customer = _cache.getItem<CustomerModels.Customer>(_cache.Key.contact);
  const startDate = now('YYYY-MM-DD') as string;
  const planAmount =
    plan?.amount || product.retailCost || product.dealerCost || 0;
  const finance = await financeDetails(startDate, planAmount);
  const discount = await getDiscount();
  let options = [] as Surcharge[];
  if (product.covCode !== undefined) {
    const planSurcharges = await getCoverageItems(product.covCode);
    options = await updateOptions(planSurcharges);
  }
  const data: PaymentModels.default = {
    amount: planAmount,
    payments: plan?.payments,
    rate: product?.taxRate || 0,
    tax: product?.taxAmount || 0,
    fees: 0,
    state: customer?.homeAddress?.state ?? '',
    startDate: startDate,
    otherAmount: discount,
    docFeeRate: finance?.product?.flDocStampFee?.rate,
    docFeeMultiple: finance?.product?.flDocStampFee?.multiples,
    options: options
  };

  const paymentPlan = await getPlan(data);
  const newPlan: PaymentModels.Plan = {
    ...plan,
    ...paymentPlan,
    updated: isoDate(),
    timestamp: timestamp()
  };

  _cache.setItem<PaymentModels.Plan>(_cache.Key.plan, newPlan);
  _cache.setItem<ProductModels.default>(_cache.Key.product, {
    ...product,
    taxRate: paymentPlan.rate,
    taxAmount: paymentPlan.tax
  });

  // check for plan errors
  if (paymentPlan.status === -1) {
    const type = 'notAllowed';
    const error = new Error(type);
    error.name = type;
    throw error;
  }

  return paymentPlan;
};

/**
 * Gets the payment plan from the cache
 * @returns the current payment plan
 */
export const getPlanInformation = async (): Promise<PaymentModels.Plan> => {
  const plan = _cache.getItem<PaymentModels.Plan>(_cache.Key.plan);
  return plan;
};

/**
 * returns the payment plan information
 * @returns the current payment plan
 */
export const getPaymentPlan = async (): Promise<PaymentModels.default> => {
  const plan = await getPlanInformation();

  const payment: PaymentModels.default = {
    amount:
      plan?.paymentType === PaymentModels.PaymentType.Full
        ? plan?.amount
        : plan?.monthlyAmount,
    tax:
      plan?.paymentType === PaymentModels.PaymentType.Full
        ? plan?.tax
        : plan?.monthlyTax,
    payments:
      plan?.paymentType === PaymentModels.PaymentType.Full
        ? 1
        : plan?.installments,
    rate: plan?.rate,
    startDate: plan?.startDate,
    nextPaymentDate: plan?.nextPaymentDate,
    paymentType: plan?.paymentType,
    payNow:
      plan?.paymentType === PaymentModels.PaymentType.Full
        ? plan?.totalWithTaxes ?? 0
        : plan?.downWithTaxes ?? 0
  };

  return payment;
};

/**
 * Gets the financial plan which is used to submit a contract. This
 * defines the monthly payment information
 * @returns the financial model
 */
export const getFinancialPlan = async (): Promise<PaymentModels.FinancialPlan | null> => {
  const { PaymentType } = PaymentModels;

  const plan = await getPlanInformation();

  let finance: PaymentModels.FinancialPlan | null = null;

  if (plan?.paymentType === PaymentType.Down) {
    finance = {
      startDate: plan.startDate,
      amount: plan.amount,
      down: plan.down,
      downRate: plan.downRate,
      fees: plan.fees,
      docFee: plan.docFee,
      payments: plan.payments,
      action: ''
    };
  }

  return finance;
};

/**
 * Gets single and monthly costs.
 * @param planCost Retail cost.
 * @param paymentPlan Payment plan.
 * @returns Monthly and single cost.
 */
export const getPlanCosts = (
  planCost: string | number,
  paymentPlan: PaymentModels.Plan | undefined
): Models.IPlanCosts => {
  // Cost comes from retail price of the current plan.
  const cost = Number(planCost);

  let otherAmount = paymentPlan?.otherAmount ?? 0;
  // Add the hidden adjustment.
  otherAmount += paymentPlan?.hiddenAdjustments ?? 0;

  // if otherAmount is negative it should be used as discount when submit the contract.
  // if it is a positive value it is a surcharge and it should not be included as discount.
  const adjustment = otherAmount;

  const singleCost = cost + (adjustment ?? 0);
  const down = singleCost * (paymentPlan?.downRate ?? 0);
  const monthlyCost = (singleCost - down) / (paymentPlan?.installments ?? 1);
  const selectedCost = paymentPlan?.installments === 1 ? singleCost : monthlyCost;

  return { monthlyCost, singleCost, selectedCost };
};

/**
 * Update Payment and return the updated plan
 */
export const updatePaymentPlan = async (isSinglePayment: boolean): Promise<PaymentModels.Plan> => {
  const plan = _cache.getItem<Models.IPlan>(_cache.Key.plan);
  _cache.setItem<Models.IPlan>(_cache.Key.plan, {
    ...plan,
    id: isSinglePayment ? 'single-payment' : 'montly-payment',
    amount: plan.amount ?? 0,
    payments: isSinglePayment ? 1 : 0 // any other than 1 uses monthly terms from API
  });
  return await setPaymentPlan();
};

export const isSinglePayment = async (): Promise<boolean> => {
  const plan = await getPlanInformation();
  return plan.payments === 1;
};
